import React, { useState, useEffect, useContext } from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '@/components/ui/dialog';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Eye, DollarSign, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const ClientCommunicationManagement = () => {
  const { token } = useContext(AuthContext);
  const [communications, setCommunications] = useState([]);
  const [filteredCommunications, setFilteredCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterView, setFilterView] = useState('all'); // all, billable, non-billable, invoiced
  const [selectedComm, setSelectedComm] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [stats, setStats] = useState(null);

  // Form state for updating billing
  const [billingStatus, setBillingStatus] = useState('');
  const [quickbooksInvoice, setQuickbooksInvoice] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [token]);

  useEffect(() => {
    filterCommunications();
  }, [searchTerm, filterView, communications]);

  const fetchData = async () => {
    try {
      setLoading(true);
      let endpoint = `${API}/client-communications`;
      
      // Use specific endpoints for filtered views
      if (filterView === 'billable') {
        endpoint = `${API}/client-communications/billable-action-needed`;
      } else if (filterView === 'non-billable') {
        endpoint = `${API}/client-communications/non-billable`;
      } else if (filterView === 'invoiced') {
        endpoint = `${API}/client-communications/invoiced`;
      }
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommunications(response.data || []);
    } catch (error) {
      console.error('Failed to fetch communications:', error);
      toast.error('Failed to load communications');
      setCommunications([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/client-communications/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const filterCommunications = () => {
    let filtered = communications;
    
    if (searchTerm) {
      filtered = filtered.filter(comm =>
        comm.work_order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comm.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        comm.service_location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredCommunications(filtered);
  };

  const handleViewDetails = (comm) => {
    setSelectedComm(comm);
    setBillingStatus(comm.billing_status || '');
    setQuickbooksInvoice(comm.quickbooks_invoice_number || '');
    setAdminNotes(comm.admin_notes || '');
    setShowDetailDialog(true);
  };

  const handleUpdateBilling = async () => {
    try {
      await axios.put(
        `${API}/client-communications/${selectedComm.id}`,
        {
          billing_status: billingStatus,
          quickbooks_invoice_number: quickbooksInvoice,
          admin_notes: adminNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Communication updated successfully');
      setShowDetailDialog(false);
      fetchData();
      fetchStats();
    } catch (error) {
      console.error('Failed to update communication:', error);
      toast.error('Failed to update communication');
    }
  };

  const getBillingStatusBadge = (status) => {
    const statusColors = {
      'Pending Review': 'bg-yellow-100 text-yellow-800',
      'Ready for Invoice': 'bg-blue-100 text-blue-800',
      'Invoiced (QuickBooks)': 'bg-green-100 text-green-800',
      'Sent to Client': 'bg-purple-100 text-purple-800',
      'Not Billable / No Charge': 'bg-gray-100 text-gray-800',
      'Closed': 'bg-slate-100 text-slate-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="p-4 space-y-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Client Communications</h1>
          <p className="text-sm text-gray-600 mt-1">Manage work order completion reports and billing</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Communications</p>
                  <p className="text-2xl font-bold">{stats.total_communications}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Billable - Action Needed</p>
                  <p className="text-2xl font-bold text-red-600">{stats.billable_action_needed}</p>
                </div>
                <DollarSign className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Invoiced / Sent</p>
                  <p className="text-2xl font-bold text-green-600">{stats.invoiced_sent}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Follow-up Needed</p>
                  <p className="text-2xl font-bold">{stats.follow_up_needed}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter Tabs */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-2 mb-4">
            <Button
              variant={filterView === 'all' ? 'default' : 'outline'}
              onClick={() => { setFilterView('all'); fetchData(); }}
              size="sm"
            >
              All
            </Button>
            <Button
              variant={filterView === 'billable' ? 'default' : 'outline'}
              onClick={() => { setFilterView('billable'); fetchData(); }}
              size="sm"
              className="text-red-600"
            >
              Billable - Action Needed
            </Button>
            <Button
              variant={filterView === 'non-billable' ? 'default' : 'outline'}
              onClick={() => { setFilterView('non-billable'); fetchData(); }}
              size="sm"
            >
              Non-Billable
            </Button>
            <Button
              variant={filterView === 'invoiced' ? 'default' : 'outline'}
              onClick={() => { setFilterView('invoiced'); fetchData(); }}
              size="sm"
            >
              Invoiced / Sent
            </Button>
          </div>
          
          <Input
            placeholder="Search by work order number, customer, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </CardContent>
      </Card>

      {/* Communications Table */}
      <Card>
        <CardContent className="pt-4">
          {filteredCommunications.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No communications found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>WO Number</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Completion Date</TableHead>
                    <TableHead>Billable</TableHead>
                    <TableHead>Billing Status</TableHead>
                    <TableHead>QB Invoice</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCommunications.map((comm) => (
                    <TableRow key={comm.id}>
                      <TableCell className="font-medium">{comm.work_order_number}</TableCell>
                      <TableCell>{comm.customer_name}</TableCell>
                      <TableCell>{comm.service_location}</TableCell>
                      <TableCell>
                        {_fmtDate(comm.completion_date)}
                      </TableCell>
                      <TableCell>
                        {comm.is_billable ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-gray-400" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={getBillingStatusBadge(comm.billing_status)}>
                          {comm.billing_status}
                        </Badge>
                      </TableCell>
                      <TableCell>{comm.quickbooks_invoice_number || '-'}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(comm)}
                        >
                          <Eye size={16} className="mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Work Order Communication Details</DialogTitle>
          </DialogHeader>
          {selectedComm && (
            <div className="space-y-6">
              {/* Work Order Info */}
              <div>
                <h3 className="font-semibold text-lg mb-3 border-b pb-2">Work Order Information</h3>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="font-medium text-gray-600">Work Order Number:</dt>
                    <dd>{selectedComm.work_order_number}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-600">Customer:</dt>
                    <dd>{selectedComm.customer_name}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-600">Service Location:</dt>
                    <dd>{selectedComm.service_location}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-600">Completion Date:</dt>
                    <dd>{_fmtDateTime(selectedComm.completion_date)}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-600">Customer Contact:</dt>
                    <dd>{selectedComm.customer_contact_name || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-600">Customer Email:</dt>
                    <dd>{selectedComm.customer_email || 'N/A'}</dd>
                  </div>
                </dl>
              </div>

              {/* Billing Controls */}
              <div>
                <h3 className="font-semibold text-lg mb-3 border-b pb-2">Billing Management</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Billable Status</Label>
                    <div className="mt-1">
                      {selectedComm.is_billable ? (
                        <Badge className="bg-green-100 text-green-800">Billable</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-800">Not Billable</Badge>
                      )}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="billing_status">Billing Status</Label>
                    <Select value={billingStatus} onValueChange={setBillingStatus}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pending Review">Pending Review</SelectItem>
                        <SelectItem value="Ready for Invoice">Ready for Invoice</SelectItem>
                        <SelectItem value="Invoiced (QuickBooks)">Invoiced (QuickBooks)</SelectItem>
                        <SelectItem value="Sent to Client">Sent to Client</SelectItem>
                        <SelectItem value="Not Billable / No Charge">Not Billable / No Charge</SelectItem>
                        <SelectItem value="Closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="quickbooks_invoice">QuickBooks Invoice Number</Label>
                    <Input
                      id="quickbooks_invoice"
                      value={quickbooksInvoice}
                      onChange={(e) => setQuickbooksInvoice(e.target.value)}
                      placeholder="Enter QB invoice number"
                    />
                  </div>

                  <div>
                    <Label htmlFor="admin_notes">Admin Notes (Internal Only)</Label>
                    <Textarea
                      id="admin_notes"
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={3}
                      placeholder="Internal notes for billing and follow-up"
                    />
                  </div>
                </div>
              </div>

              {/* Photos Section */}
              {(selectedComm.before_photos?.length > 0 || selectedComm.after_photos?.length > 0) && (
                <div>
                  <h3 className="font-semibold text-lg mb-3 border-b pb-2">Photos</h3>
                  {selectedComm.before_photos?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-600 mb-2">Before Photos:</p>
                      <div className="text-sm text-gray-500">
                        {selectedComm.before_photos.length} photo(s) available
                      </div>
                    </div>
                  )}
                  {selectedComm.after_photos?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-600 mb-2">After Photos:</p>
                      <div className="text-sm text-gray-500">
                        {selectedComm.after_photos.length} photo(s) available
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateBilling} className="bg-[#A8D5BA] hover:bg-[#8FC9A9] text-gray-800">
              Update Billing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientCommunicationManagement;
